/**
 * Strategy Comparison product page — BP-014.
 * Every exit strategy scored side-by-side with a Why-Not audit.
 */
import { Target, GitCompare, Scale, ShieldQuestion, TrendingUp, Layers } from "lucide-react";
import ProductPageTemplate, { ProductScreen } from "../ProductPageTemplate";

const heroMockup = (
  <ProductScreen title="strategy / compare · 42 Coach Test Ln">
    <div className="space-y-2 text-xs">
      {[
        ["Subject-To", 87, "+$41k EV", "WINNER", true],
        ["Novation", 78, "+$28k EV", "runner-up"],
        ["Wholesale", 71, "+$14k EV", "safe"],
        ["Fix & Flip", 62, "+$8k EV", "capital-heavy"],
      ].map(([name, score, ev, tag, winner]) => (
        <div
          key={name}
          className={`flex items-center gap-2 rounded-lg p-2 ${winner ? "border-2 border-emerald-500 bg-emerald-50" : "border border-slate-200"}`}
        >
          <div className={`w-8 h-8 rounded-md text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${winner ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600"}`}>
            {String(score)}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900">{name}</div>
            <div className="text-[10px] text-slate-500">{tag}</div>
          </div>
          <div className="font-mono text-emerald-700 font-semibold">{ev}</div>
        </div>
      ))}
    </div>
    <div className="mt-2 text-[10px] text-slate-500 border-t border-slate-200 pt-2">
      <span className="font-semibold text-slate-700">Why-Not audit:</span> Fix &amp; Flip lost on capital deployed (−22 pts) &amp; hold time (−9 pts)
    </div>
  </ProductScreen>
);

export default function StrategyComparisonPage() {
  return (
    <ProductPageTemplate
      slug="strategy-comparison"
      bp="BP-014 · Strategy Comparison Engine"
      eyebrow="Product · Strategy Comparison"
      title="Every strategy, scored."
      titleAccent="Every rejection, explained."
      subtitle="Wholesale, Novation, Subject-To, Seller-Finance, Wholetail, Fix &amp; Flip, BRRRR, Buy-and-Hold — every strategy is scored side-by-side per property with a numeric Why-Not audit for every rejected exit."
      bullets={[
        "8 exit strategies scored in parallel — no strategy is silently ignored",
        "Expected value (EV) computed with capital, time, and risk penalties",
        "Why-Not panel explains every rejected strategy with numeric attribution",
      ]}
      heroMockup={heroMockup}
      capabilities={[
        { icon: Target, title: "8 Strategies, One Model", desc: "Wholesale, Novation, Subject-To, Seller-Finance, Wholetail, Flip, BRRRR, Buy-and-Hold — parallel scoring." },
        { icon: GitCompare, title: "Side-by-Side EV", desc: "Expected value adjusted for capital deployed, hold time, and downside risk — apples-to-apples." },
        { icon: ShieldQuestion, title: "Why-Not Audit", desc: "Every rejected strategy comes with a numeric reason — ends the 'but we should flip it' arguments." },
        { icon: Scale, title: "Factor Weights", desc: "Configurable per tenant — cash-heavy investors weight capital, wholesalers weight speed. Governed by admin." },
        { icon: TrendingUp, title: "Sensitivity Panel", desc: "See how ranking shifts with ±10% ARV, ±20% Repair, ±5% rate. Stress-test in one click." },
        { icon: Layers, title: "Coach-Integrated", desc: "The AI Coach cites Strategy Comparison as its primary evidence source — this is the engine that decides." },
      ]}
      howItWorks={[
        { step: 1, title: "Extract", desc: "ARV, Repair, Rent, Comp velocity, and Market tier flow in from upstream engines." },
        { step: 2, title: "Score", desc: "Each of the 8 strategies runs its own EV + risk model, adjusted for capital and hold time." },
        { step: 3, title: "Rank", desc: "Winner and top-3 surface. Losers get a numeric Why-Not attribution." },
        { step: 4, title: "Cite", desc: "Coach picks up the ranking, cites versioned outputs, presents the recommendation." },
      ]}
      integrations={[
        { bp: "BP-014", name: "Strategy Comparison" },
        { bp: "BP-003", name: "Repair Estimator" },
        { bp: "BP-004", name: "ARV Comps" },
        { bp: "BP-005", name: "Deal Score" },
        { bp: "BP-021", name: "Cash Flow" },
        { bp: "BP-022", name: "Buyer Intelligence" },
        { bp: "BP-024", name: "Market Intelligence" },
        { bp: "BP-025", name: "AI Coach" },
      ]}
      proof={{
        stat: "8",
        label: "exit strategies scored in parallel per property",
        quote: "The Why-Not panel ended debates in our Monday call. Every rejected strategy had a number attached. We stopped losing time to opinion.",
        attribution: "Acquisition team lead · 18-seat team",
      }}
      related={[
        { title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "The Coach synthesises Strategy Comparison into a plain-English recommendation." },
        { title: "Buyer Intelligence", to: "/products/buyer-intelligence", desc: "Once a strategy wins, ranked buyers surface automatically." },
        { title: "Portfolio Intelligence", to: "/products/portfolio-intelligence", desc: "Re-score exit strategies against held assets for hold-vs-sell decisions." },
      ]}
    />
  );
}
